import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";
import { haptic } from "../telegram";
import { useBackNavigation } from "../use-back-navigation";

type NotificationSettingsValue = {
  likes: boolean;
  matches: boolean;
  messages: boolean;
  superlikes: boolean;
  team: boolean;
};

const categories: Array<{ key: keyof NotificationSettingsValue; title: string; description: string }> = [
  { key: "messages", title: "Сообщения", description: "Уведомления о новых сообщениях" },
  { key: "matches", title: "Новые пары", description: "Уведомления о новых совпадениях" },
  { key: "likes", title: "Лайки", description: "Уведомления о новых лайках" },
  { key: "superlikes", title: "Суперлайки", description: "Уведомления о новых суперлайках" },
  { key: "team", title: "Команда DonDate", description: "Новости и объявления сервиса" },
];

export function NotificationSettings({ onClose }: { onClose(): void }) {
  const [value, setValue] = useState<NotificationSettingsValue | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const panelRef = useRef<HTMLElement>(null);
  useBackNavigation(onClose, !saving, panelRef);
  const closeButton = useRef<HTMLButtonElement>(null);
  const savingRef = useRef(false);
  savingRef.current = saving;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setValue({
        likes: true,
        matches: true,
        messages: true,
        superlikes: true,
        team: false,
        ...(await api<Partial<NotificationSettingsValue>>("/v1/me/notification-settings")),
      });
    } catch {
      setError("Не удалось загрузить настройки. Проверьте соединение.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButton.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !savingRef.current) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [onClose]);

  const save = async () => {
    if (!value || saving) return;
    setSaving(true);
    setError("");
    try {
      await api("/v1/me/notification-settings", {
        method: "PUT",
        body: JSON.stringify(value),
      });
      haptic("success");
      onClose();
    } catch {
      setError("Не удалось сохранить настройки. Попробуйте ещё раз.");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="notification-settings-layer">
      <button
        type="button"
        className="notification-settings-backdrop"
        aria-label="Закрыть настройки уведомлений"
        onClick={() => {
          if (!saving) onClose();
        }}
      />
      <section
        ref={panelRef}
        className="notification-settings-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="notification-settings-title"
      >
        <header className="notification-settings-header">
          <div>
            <span className="eyebrow">Профиль</span>
            <h2 id="notification-settings-title">Настройки уведомлений</h2>
          </div>
          <button ref={closeButton} type="button" onClick={onClose} disabled={saving} aria-label="Назад">
            ‹
          </button>
        </header>

        {loading ? (
          <p className="notification-settings-status" role="status">
            Загружаем настройки…
          </p>
        ) : !value ? (
          <div className="notification-settings-status">
            <p role="alert">{error}</p>
            <button type="button" className="secondary-button" onClick={() => void load()}>
              Повторить
            </button>
          </div>
        ) : (
          <>
            <p className="notification-settings-intro">
              Выберите события, которые должны попадать в центр уведомлений DonDate.
            </p>
            <div className="notification-settings-options">
              {categories.map((item) => (
                <label className="notification-settings-option" key={item.key}>
                  <span>
                    <strong>{item.title}</strong>
                    <small>{item.description}</small>
                  </span>
                  <input
                    type="checkbox"
                    checked={value[item.key]}
                    disabled={saving}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setValue((current) => (current ? { ...current, [item.key]: checked } : current));
                    }}
                  />
                </label>
              ))}
            </div>
            <p className="notification-settings-safety">
              Уведомления о модерации и безопасности аккаунта всегда сохраняются. Сообщения и совпадения остаются
              доступными в своих разделах, даже если отключить уведомления.
            </p>
            {error && (
              <p className="notification-settings-error" role="alert">
                {error}
              </p>
            )}
            <div className="notification-settings-actions">
              <button type="button" className="secondary-button" disabled={saving} onClick={onClose}>
                Отмена
              </button>
              <button type="button" className="primary-button" disabled={saving} onClick={() => void save()}>
                {saving ? "Сохраняем…" : "Сохранить"}
              </button>
            </div>
          </>
        )}
      </section>
    </div>,
    document.body,
  );
}
